import { useCallback, useEffect, useState } from 'react'
import { api, type SentryWebhookInfo } from '../api/client'

type Props = {
  getAuthToken: () => Promise<string>
  refreshTrigger: number
}

export function SentryWebhookSetup({ getAuthToken, refreshTrigger }: Props) {
  const [info, setInfo] = useState<SentryWebhookInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const token = await getAuthToken()
      const data = await api.getSentryWebhook(token)
      setInfo(data)
    } catch (err) {
      setError((err as Error).message)
      setInfo(null)
    } finally {
      setLoading(false)
    }
  }, [getAuthToken])

  useEffect(() => {
    void load()
  }, [load, refreshTrigger])

  async function handleCopy() {
    if (!info?.webhook_url) return
    try {
      await navigator.clipboard.writeText(info.webhook_url)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  return (
    <div className="sentry-webhook-panel">
      <h3 className="sentry-webhook-title">Receive Sentry alerts</h3>
      <p className="sentry-webhook-intro">
        When a Sentry alert fires, SelfHeal automatically starts an investigation using your API credentials.
      </p>

      <ol className="sentry-webhook-steps">
        <li>
          Save your <strong>Organization slug</strong> and <strong>Auth token</strong> — needed to read stack traces during investigations.
        </li>
        <li>
          Create a random <strong>Webhook secret</strong>, save it, then reload this panel to see the full webhook URL.
        </li>
        <li>
          In Sentry, create an <strong>Internal Integration</strong> (Settings → Developer Settings). Add this webhook URL and enable <strong>Alert Rule Action</strong>. Then in Issue Alerts, choose <strong>Send a notification via SelfHeal</strong>. Don't use Legacy integrations.
        </li>
        <li>
          Optional: set a <strong>Project slug</strong> to limit alerts to one project.
        </li>
      </ol>

      {loading && <p className="sentry-webhook-muted">Loading webhook URL…</p>}
      {error && <p className="cred-error">Could not load webhook URL: {error}</p>}

      {info && !loading && (
        <>
          {!info.webhook_secret_configured && (
            <p className="sentry-webhook-hint">
              The URL above contains a placeholder. Create a random webhook secret, save it in the credentials form, then copy the updated URL here.
            </p>
          )}
          {!info.api_credentials_configured && (
            <p className="sentry-webhook-hint">
              Save your org slug and auth token above — SelfHeal needs them to read Sentry during investigations.
            </p>
          )}
          <div className="sentry-webhook-url-row">
            <input
              className="cred-input sentry-webhook-url-input"
              type="text"
              readOnly
              value={info.webhook_url}
              aria-label="Sentry webhook URL"
              onFocus={(e) => e.target.select()}
            />
            <button
              type="button"
              className="sentry-webhook-copy-btn"
              onClick={() => void handleCopy()}
              disabled={!info.webhook_url}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <p className="sentry-webhook-muted">
            Organization ID: <code>{info.tenant_id}</code>
          </p>
        </>
      )}
    </div>
  )
}
